/* eslint-disable react/prop-types */
"use client";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { CHAT_STICKERS } from "../../constants/chatStickers";
import { isNativeAndroid, isNativeApp } from "../../native/runtime";

const CHAT_ICON_FILTER_WHITE =
  "brightness(0) saturate(100%) invert(100%)";
const CHAT_ICON_FILTER_BLACK =
  "brightness(0) saturate(100%)";
const COMPOSER_MIN_HEIGHT = 58;
const COMPOSER_LINE_HEIGHT = 20;
const COMPOSER_VERTICAL_PADDING = 8;
const COMPOSER_COLLAPSED_VERTICAL_PADDING = 19;
const COMPOSER_MAX_LINES = 8;
const COMPOSER_MAX_HEIGHT =
  COMPOSER_LINE_HEIGHT * COMPOSER_MAX_LINES + COMPOSER_VERTICAL_PADDING * 2;
const NATIVE_FOCUS_RECOVERY_DELAY_MS = 80;
const CANCELLED_FEED_TOUCH_SETTLE_MS = 250;
const WEB_KEYBOARD_OPEN_THRESHOLD_PX = 120;
const ANDROID_EXTERNAL_POINTER_GUARD_MS = 400;

const isWebKeyboardVisible = () => {
  const visualHeight = Number(window.visualViewport?.height || 0);
  const layoutHeight = Math.max(
    Number(window.innerHeight || 0),
    Number(document.documentElement?.clientHeight || 0)
  );
  return visualHeight > 0 &&
    layoutHeight - visualHeight > WEB_KEYBOARD_OPEN_THRESHOLD_PX;
};

/**
 * ChatInputBar
 *
 * Dumb UI + minimal behavior component.
 * All business logic (send, edit, reply, media rules) is controlled by props.
 *
 * This is intentionally reusable for:
 * - Event chats (with media)
 * - Direct messages (no media)
 */
export default function ChatInputBar({
  t,

  // refs
  inputRef,
  fileInputRef,
  wrapRef,

  // state
  inputValue,
  sending,
  replyTo,
  editingId,
  editingMeta,
  pendingImages = [],
  unreadCount = 0,
  typingUsers = [],
  showTypingIndicator = true,
  overlayDimmed = false,
  primaryCtaLabel = "",
  onPrimaryCtaClick = null,
  secondaryCtaLabel = "",
  onSecondaryCtaClick = null,
  embedded = false,

  // capabilities
  allowAttachments = true,
  attachmentsUnlocked = true,
  photoOnlyAttachments = false,
  canSendMessages = true,
  sendDisabledMessage = "",
  hideComposerWhenDisabled = false,
  leftActionButtons = [],
  supportIdentityChoice = false,
  sendWithPersonalPhoto = false,
  onSupportIdentityChange,

  // handlers
  onInputChange,
  onInputKeyDown,
  onSend,
  onSendSticker,
  onPickImages,
  onOpenNativeImagePicker,
  photoSourceMenuRequestId = 0,
  onOpenPoll,
  onOpenLocation,
  onLockedAttachmentsClick,
  onRemoveImage,
  onCancelContext,
  onJumpToUnread,
  onOverlayDismiss,
}) {
  const localInputRef = useRef(null);
  const localFileInputRef = useRef(null);
  const measureInputRef = useRef(null);
  const stickerPanelRef = useRef(null);
  const stickerToggleRef = useRef(null);
  const stickerScrollRef = useRef(null);
  const photoSourcePanelRef = useRef(null);
  const focusRecoveryTimerRef = useRef(null);
  const lastExternalPointerDownAtRef = useRef(0);
  const lastFeedPointerDownAtRef = useRef(0);
  const feedGestureActiveRef = useRef(false);
  const feedTouchGestureActiveRef = useRef(false);
  const feedScrollSettleTimerRef = useRef(null);
  const pendingFeedFocusRecoveryRef = useRef(null);
  const lastPhotoSourceMenuRequestRef = useRef(photoSourceMenuRequestId);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [stickerPanelOpen, setStickerPanelOpen] = useState(false);
  const [photoSourceMenuOpen, setPhotoSourceMenuOpen] = useState(false);
  const [stickerScrollMetrics, setStickerScrollMetrics] = useState({
    canScroll: false,
    thumbHeight: 36,
    thumbTop: 0,
  });
  const [composerHeight, setComposerHeight] = useState(COMPOSER_MIN_HEIGHT);
  const [composerCollapsed, setComposerCollapsed] = useState(true);

  // Avoid fighting IME composition (JP/CN/KR) while composing.
  const composingRef = useRef(false);

  const secondCharForcedUpperRef = useRef(false);

  const lastValueRef = useRef(String(inputValue || ""));

  // Keep the ref in sync when parent resets the value (send/cancel/edit etc.)
  useEffect(() => {
    lastValueRef.current = String(inputValue || "");
  }, [inputValue]);

  // iOS Safari/WKWebView sometimes keeps Shift active for the first TWO letters
  // when using auto-capitalization with a controlled textarea.
  const isIOS =
    typeof window !== "undefined" &&
    (/iPhone|iPad|iPod/i.test(String(navigator?.userAgent || "")) ||
      (/Macintosh|Mac OS X/i.test(String(navigator?.userAgent || "")) &&
        Number(navigator?.maxTouchPoints) > 1));

  const isLetter = (ch) => {
    if (!ch) return false;
    const s = String(ch);
    return s.toLowerCase() !== s.toUpperCase();
  };

  // Measure on a hidden twin instead of toggling the visible textarea to
  // height:auto. On iOS the visible auto->fixed cycle can flicker while typing.
  const applyComposerSize = useCallback((rawValue, targetEl) => {
    const value = String(rawValue ?? "");
    const el = targetEl || localInputRef.current;
    const measureEl = measureInputRef.current;
    let scrollHeight = COMPOSER_MIN_HEIGHT;
    let nextCollapsed = true;

    if (measureEl) {
      measureEl.value = value || " ";
      measureEl.style.height = "auto";
      measureEl.style.paddingTop = `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`;
      measureEl.style.paddingBottom = `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`;

      const collapsedScrollHeight = measureEl.scrollHeight || COMPOSER_MIN_HEIGHT;
      nextCollapsed = collapsedScrollHeight <= COMPOSER_MIN_HEIGHT;

      if (nextCollapsed) {
        scrollHeight = COMPOSER_MIN_HEIGHT;
      } else {
        measureEl.style.paddingTop = `${COMPOSER_VERTICAL_PADDING}px`;
        measureEl.style.paddingBottom = `${COMPOSER_VERTICAL_PADDING}px`;
        measureEl.style.height = "auto";
        scrollHeight = measureEl.scrollHeight || COMPOSER_MIN_HEIGHT;
      }
    } else if (el) {
      const previousHeight = el.style.height;
      el.style.paddingTop = `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`;
      el.style.paddingBottom = `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`;
      el.style.height = "auto";
      const collapsedScrollHeight = el.scrollHeight || COMPOSER_MIN_HEIGHT;
      nextCollapsed = collapsedScrollHeight <= COMPOSER_MIN_HEIGHT;

      if (nextCollapsed) {
        scrollHeight = COMPOSER_MIN_HEIGHT;
      } else {
        el.style.paddingTop = `${COMPOSER_VERTICAL_PADDING}px`;
        el.style.paddingBottom = `${COMPOSER_VERTICAL_PADDING}px`;
        el.style.height = "auto";
        scrollHeight = el.scrollHeight || COMPOSER_MIN_HEIGHT;
      }

      el.style.height = previousHeight;
    }

    const nextHeight = Math.max(
      COMPOSER_MIN_HEIGHT,
      Math.min(COMPOSER_MAX_HEIGHT, scrollHeight)
    );

    if (el) {
      el.style.height = `${nextHeight}px`;
      el.style.overflowY = scrollHeight > COMPOSER_MAX_HEIGHT ? "auto" : "hidden";
      el.style.paddingTop = `${
        nextCollapsed ? COMPOSER_COLLAPSED_VERTICAL_PADDING : COMPOSER_VERTICAL_PADDING
      }px`;
      el.style.paddingBottom = `${
        nextCollapsed ? COMPOSER_COLLAPSED_VERTICAL_PADDING : COMPOSER_VERTICAL_PADDING
      }px`;
    }

    setComposerHeight((prev) => (prev === nextHeight ? prev : nextHeight));
    setComposerCollapsed((prev) => (prev === nextCollapsed ? prev : nextCollapsed));
  }, []);

  const handlePatchedChange = (e) => {
    const elFromEvent = e?.target;
    const next = String(elFromEvent?.value ?? "");

    if (isIOS) {
      const prev = String(lastValueRef.current || "");

      // Patch the common iOS bug: "HEllo" instead of "Hello" at the start.
      // Only when the user is typing forward (caret at end) and only for 2nd char.
      const el = localInputRef.current || inputRef?.current;
      const caretAtEnd =
        typeof el?.selectionStart === "number" ? el.selectionStart === next.length : true;

      // If user explicitly forced the second char uppercase (Shift on second key),
      // we skip the auto-lowercase patch once.
      const skipPatch = secondCharForcedUpperRef.current;
      if (skipPatch) {
        secondCharForcedUpperRef.current = false;
      }

      if (
        !skipPatch &&
        caretAtEnd &&
        prev.length === 1 &&
        next.length === 2 &&
        next[0] === prev[0] &&
        isLetter(next[0]) &&
        isLetter(next[1]) &&
        next[0] === next[0].toUpperCase() &&
        next[1] === next[1].toUpperCase()
      ) {
        const patched = next[0] + next[1].toLowerCase() + next.slice(2);
        lastValueRef.current = patched;
        try {
          // Ensure parent handler reads patched value
          e.target.value = patched;

          // Keep caret at the end when we patch the second character.
          // (This patch is only applied when caret is already at end.)
        } catch {
          // ignore
        }
        applyComposerSize(patched, e.target);
        onInputChange?.(e);
        return;
      }
    }

    lastValueRef.current = next;
    applyComposerSize(next, elFromEvent);
    onInputChange?.(e);
  };

  const syncComposerDomValue = useCallback((input) => {
    if (!input) return;
    const next = String(input.value ?? "");
    if (next === lastValueRef.current) return;

    lastValueRef.current = next;
    applyComposerSize(next, input);
    // Some Android IMEs commit an emoji immediately before dropping WebView
    // focus without delivering React's input event. Preserve that DOM value in
    // the parent draft before the next render can replace it.
    onInputChange?.({ target: input, currentTarget: input });
  }, [applyComposerSize, onInputChange]);

  const handleKeyDown = (e) => {
    if (isIOS) {
      const el = localInputRef.current || inputRef?.current;
      const currentValue = String(el?.value ?? "");
      const caretAtEnd =
        typeof el?.selectionStart === "number"
          ? el.selectionStart === currentValue.length
          : true;

      // If there is exactly one character already, caret is at end,
      // and the user presses Shift+Letter producing an uppercase second char,
      // treat this as intentional and skip the auto-lowercase patch once.
      if (
        caretAtEnd &&
        lastValueRef.current.length === 1 &&
        isLetter(e.key) &&
        e.key === e.key.toUpperCase() &&
        e.shiftKey
      ) {
        secondCharForcedUpperRef.current = true;
      }
    }

    onInputKeyDown?.(e);
  };

  // Autosize the composer up to eight lines. After that the draft scrolls inside
  // the textarea, matching the older chat behavior without letting the input
  // cover the conversation.
  useLayoutEffect(() => {
    const el = localInputRef.current || inputRef?.current;
    if (!el) return;

    // An empty DOM value is meaningful after send/delete. Do not fall back to
    // a stale parent value or the composer can remain expanded with only the
    // placeholder visible.
    applyComposerSize(el.value ?? inputValue ?? "", el);
  }, [applyComposerSize, inputValue, inputRef]);

  // Keep an uncontrolled textarea in sync when parent changes inputValue
  // (e.g. send/reset/edit/cancel). During typing, `el.value` already matches
  // and we do nothing, which prevents caret "flash".
  useLayoutEffect(() => {
    if (composingRef.current) return;

    const el = localInputRef.current || inputRef?.current;
    if (!el) return;

    const external = String(inputValue || "");
    const current = String(el.value || "");

    // If user is typing, DOM already has the latest value.
    if (external === current) return;

    el.value = external;
    lastValueRef.current = external;
    applyComposerSize(external, el);

    // For explicit external updates, keep caret at end.
    try {
      const end = external.length;
      el.setSelectionRange(end, end);
    } catch {
      // ignore
    }
  }, [applyComposerSize, inputValue, inputRef]);

  const hasText = !!String(inputValue || "").trim();
  const hasImages = (pendingImages?.length || 0) > 0;
  // When editing an existing message we only allow sending when there is text.
  // When composing a new message we allow sending with text OR images.
  const canSend =
    canSendMessages &&
    !sending &&
    (editingId ? hasText : (hasText || hasImages));

  const typingList = Array.isArray(typingUsers) ? typingUsers.filter(Boolean) : [];
  const visibleTypingUsers = typingList.slice(0, 3);
  const typingCount = typingList.length;
  const typingLabel = (() => {
    if (typingCount <= 0) return "";
    if (typingCount === 1) {
      const u = visibleTypingUsers[0];
      const username = String(u?.username || "").trim();
      const code = String(u?.code || "").trim();
      if (username) return `${username.startsWith("@") ? username : `@${username}`} is typing`;
      if (code) return `${code} is typing`;
      return "Typing...";
    }
    return `${typingCount} people are typing`;
  })();

  const buildTypingAvatar = (u) => {
    return resolveUserAvatarSrc(u);
  };

  const renderOverlayDismiss = (className) => {
    if (!overlayDimmed) return null;

    return (
      <button
        type="button"
        className={`absolute inset-0 z-10 bg-transparent ${className}`}
        onClick={() => onOverlayDismiss?.()}
        aria-label="Close message actions"
      />
    );
  };

  const renderUnreadJump = () => {
    const normalizedUnreadCount = Math.max(0, Math.floor(Number(unreadCount) || 0));
    if (normalizedUnreadCount <= 0) return null;

    const unreadLabel = normalizedUnreadCount > 99 ? "99+" : String(normalizedUnreadCount);

    return (
      <button
        type="button"
        onClick={onJumpToUnread}
        className="absolute right-[13px] bottom-full mb-2 flex h-11 min-w-11 items-center justify-center rounded-full bg-white px-2 text-[#002A5D] shadow-[0_2px_6px_rgba(0,0,0,0.18)]"
        aria-label={`${t("chats.tabs.unread", { defaultValue: "Unread" })}: ${normalizedUnreadCount}`}
      >
        <span className="text-[14px] font-black">{unreadLabel}</span>
      </button>
    );
  };

  const normalizedLeftActionButtons = Array.isArray(leftActionButtons)
    ? leftActionButtons.filter(Boolean)
    : [];
  const stickersEnabled = typeof onSendSticker === "function";
  const dimmedComposerStyle = overlayDimmed
    ? { opacity: 0.35 }
    : undefined;
  // Match the mobile message feed's 16px side gutters while keeping the desktop cap.
  const embeddedComposerWidth = "min(816px, calc(100% - 32px))";
  const attachmentMenuStyle = {
    ...dimmedComposerStyle,
    bottom: `${composerHeight + 14}px`,
  };
  // Keep the attachment trigger active while the current message is in flight.
  // Native uploads can take long enough that users naturally tap the paperclip
  // immediately after Send. Disabling it here drops that tap in WKWebView.
  const canInteractWithAttachments = allowAttachments && !editingId && canSendMessages;
  const canUseAttachments = canInteractWithAttachments && attachmentsUnlocked;
  const canUseSecondaryAttachments = canUseAttachments && !sending;
  const usesNativePhotoSourceMenu = Boolean(onOpenNativeImagePicker) && isNativeApp();
  const textareaPaddingClass = allowAttachments ? "pl-[48px]" : "pl-2";
  const composerTextareaBaseClass = `bg-transparent border-none outline-none resize-none overflow-x-hidden overscroll-contain touch-pan-y text-white placeholder:text-white/45 text-[15px] font-medium leading-5 ${
    stickersEnabled ? "pr-[100px]" : "pr-[62px]"
  } appearance-none [-webkit-user-select:text] [user-select:text] [-webkit-text-size-adjust:100%] [text-size-adjust:100%] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`;
  const visibleComposerTextareaClass =
    `w-full ${composerTextareaBaseClass} ${textareaPaddingClass}`;
  const measureComposerTextareaClass =
    `pointer-events-none absolute left-3 right-3 top-0 z-0 h-auto max-h-none opacity-0 ${composerTextareaBaseClass} ${textareaPaddingClass}`;

  useEffect(() => {
    if (!canUseAttachments) {
      setAttachmentMenuOpen(false);
      setPhotoSourceMenuOpen(false);
    }
  }, [canUseAttachments]);

  useEffect(() => {
    if (lastPhotoSourceMenuRequestRef.current === photoSourceMenuRequestId) return;
    lastPhotoSourceMenuRequestRef.current = photoSourceMenuRequestId;
    if (!photoSourceMenuRequestId || !canUseAttachments || !usesNativePhotoSourceMenu) return;
    setAttachmentMenuOpen(false);
    setStickerPanelOpen(false);
    setPhotoSourceMenuOpen(true);
  }, [canUseAttachments, photoSourceMenuRequestId, usesNativePhotoSourceMenu]);

  useEffect(() => {
    if (!canSendMessages || editingId || overlayDimmed) setStickerPanelOpen(false);
  }, [canSendMessages, editingId, overlayDimmed]);

  const updateStickerScrollMetrics = useCallback(() => {
    const scroller = stickerScrollRef.current;
    if (!scroller) return;

    const viewportHeight = Math.max(0, scroller.clientHeight || 0);
    const contentHeight = Math.max(viewportHeight, scroller.scrollHeight || 0);
    const canScroll = contentHeight > viewportHeight + 1;
    const trackHeight = Math.max(0, viewportHeight - 24);
    const thumbHeight = canScroll
      ? Math.max(36, Math.min(trackHeight, (viewportHeight / contentHeight) * trackHeight))
      : trackHeight;
    const maxScrollTop = Math.max(1, contentHeight - viewportHeight);
    const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
    const thumbTop = canScroll
      ? Math.min(maxThumbTop, (scroller.scrollTop / maxScrollTop) * maxThumbTop)
      : 0;

    setStickerScrollMetrics((current) => {
      const next = { canScroll, thumbHeight, thumbTop };
      if (
        current.canScroll === next.canScroll &&
        Math.abs(current.thumbHeight - next.thumbHeight) < 0.5 &&
        Math.abs(current.thumbTop - next.thumbTop) < 0.5
      ) {
        return current;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!stickerPanelOpen) return undefined;

    const frameId = window.requestAnimationFrame(updateStickerScrollMetrics);
    const scroller = stickerScrollRef.current;
    const resizeObserver =
      typeof ResizeObserver === "function" && scroller
        ? new ResizeObserver(updateStickerScrollMetrics)
        : null;
    resizeObserver?.observe(scroller);
    window.addEventListener("resize", updateStickerScrollMetrics);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateStickerScrollMetrics);
    };
  }, [stickerPanelOpen, updateStickerScrollMetrics]);

  useEffect(() => {
    if (!stickerPanelOpen) return undefined;

    const handleOutsidePointerDown = (event) => {
      const target = event.target;
      if (stickerPanelRef.current?.contains(target)) return;
      if (stickerToggleRef.current?.contains(target)) return;
      setStickerPanelOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    };
  }, [stickerPanelOpen]);

  useEffect(() => {
    if (!photoSourceMenuOpen) return undefined;

    const handleOutsidePointerDown = (event) => {
      if (photoSourcePanelRef.current?.contains(event.target)) return;
      setPhotoSourceMenuOpen(false);
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown, true);
    };
  }, [photoSourceMenuOpen]);

  const toggleStickerPanel = () => {
    if (!canSendMessages || editingId || sending) return;
    setAttachmentMenuOpen(false);
    setPhotoSourceMenuOpen(false);

    if (stickerPanelOpen) {
      setStickerPanelOpen(false);
      window.requestAnimationFrame(() => {
        const input = localInputRef.current || inputRef?.current;
        input?.focus?.({ preventScroll: true });
      });
      return;
    }

    if (focusRecoveryTimerRef.current != null) {
      window.clearTimeout(focusRecoveryTimerRef.current);
      focusRecoveryTimerRef.current = null;
    }
    // Keep the textarea focused while opening the tray. Blurring it here used
    // to start a native keyboard/VisualViewport resize at the same moment a
    // sticker was added to the feed, producing a one-frame page jump.
    setStickerPanelOpen(true);
  };

  const handleComposerFocus = () => {
    if (focusRecoveryTimerRef.current != null) {
      window.clearTimeout(focusRecoveryTimerRef.current);
      focusRecoveryTimerRef.current = null;
    }
    setStickerPanelOpen(false);
  };

  const scheduleComposerFocusRecovery = useCallback((input, {
    allowAndroidIme = false,
    allowFeedElement = false,
    allowWebKeyboard = false,
    immediate = false,
  } = {}) => {
    if (focusRecoveryTimerRef.current != null) {
      window.clearTimeout(focusRecoveryTimerRef.current);
      focusRecoveryTimerRef.current = null;
    }

    const restoreFocus = () => {
      if (!input?.isConnected || input.disabled) return;
      if (
        !input.closest?.('[data-chat-keyboard-open="true"]') &&
        !allowAndroidIme &&
        !(allowWebKeyboard && isWebKeyboardVisible())
      ) return;

      const activeElement = document.activeElement;
      const focusIsUnclaimed =
        !activeElement ||
        activeElement === document.body ||
        activeElement === document.documentElement;
      const focusStayedInFeed =
        allowFeedElement &&
        activeElement?.closest?.('[data-chat-message-feed="true"]');
      const focusStayedOnStickerControl =
        stickerToggleRef.current?.contains?.(activeElement) ||
        stickerPanelRef.current?.contains?.(activeElement);
      if (!focusIsUnclaimed && !focusStayedInFeed && !focusStayedOnStickerControl) return;

      input.focus?.({ preventScroll: true });
    };

    // A synchronous attempt at the end of a touch gesture keeps this inside
    // the native user-interaction window. The delayed verification runs after
    // WebKit's default gesture handling, which can otherwise drop that focus
    // again after pointerup/touchend.
    if (immediate) restoreFocus();
    focusRecoveryTimerRef.current = window.setTimeout(() => {
      focusRecoveryTimerRef.current = null;
      restoreFocus();
    }, NATIVE_FOCUS_RECOVERY_DELAY_MS);
  }, []);

  const handleComposerBlur = (event) => {
    const input = event.currentTarget;
    const nativeAndroid = isNativeAndroid();
    if (nativeAndroid) syncComposerDomValue(input);

    // Mobile Safari can also leave its keyboard visible after a feed drag
    // drops DOM focus. The same recovery is needed in the web chat.
    const nativeApp = isNativeApp();
    if (!nativeApp && !isIOS) return;
    const browserIOS = !nativeApp && isIOS;

    const keyboardWasOpen =
      input.closest?.('[data-chat-keyboard-open="true"]') ||
      (browserIOS && isWebKeyboardVisible());
    const feedGestureBlur =
      feedGestureActiveRef.current ||
      Date.now() - lastFeedPointerDownAtRef.current <= ANDROID_EXTERNAL_POINTER_GUARD_MS;
    const externalPointerBlur =
      !feedGestureBlur &&
      Date.now() - lastExternalPointerDownAtRef.current <= ANDROID_EXTERNAL_POINTER_GUARD_MS;
    if (feedGestureBlur) {
      // A native feed drag can make WebKit drop DOM focus without closing the
      // operating-system keyboard. Do not try to recover while the finger is
      // still moving: WebKit can discard that focus again before touchend and
      // leave the keyboard visible but detached from the textarea.
      if (!keyboardWasOpen) return;
      if (feedGestureActiveRef.current) {
        pendingFeedFocusRecoveryRef.current = input;
      } else {
        scheduleComposerFocusRecovery(input, {
          allowFeedElement: true,
          allowWebKeyboard: browserIOS,
        });
      }
      return;
    }
    if (externalPointerBlur) return;
    const androidImeBlur =
      nativeAndroid &&
      !externalPointerBlur;
    if (!keyboardWasOpen && !androidImeBlur) return;

    scheduleComposerFocusRecovery(input, {
      allowAndroidIme: androidImeBlur,
      allowWebKeyboard: browserIOS,
    });
  };

  useEffect(() => {
    const rememberExternalPointerDown = (event) => {
      const input = localInputRef.current || inputRef?.current;
      if (!input) return;
      if (feedScrollSettleTimerRef.current != null) {
        window.clearTimeout(feedScrollSettleTimerRef.current);
        feedScrollSettleTimerRef.current = null;
      }
      if (input === event.target || input.contains?.(event.target)) {
        feedGestureActiveRef.current = false;
        feedTouchGestureActiveRef.current = false;
        pendingFeedFocusRecoveryRef.current = null;
        lastExternalPointerDownAtRef.current = 0;
        lastFeedPointerDownAtRef.current = 0;
        return;
      }
      if (
        stickerToggleRef.current?.contains?.(event.target) ||
        stickerPanelRef.current?.contains?.(event.target)
      ) {
        // Sticker controls are part of the composer interaction. Some
        // WebViews still emit blur despite preventDefault on pointerdown.
        feedGestureActiveRef.current = false;
        feedTouchGestureActiveRef.current = false;
        pendingFeedFocusRecoveryRef.current = null;
        lastExternalPointerDownAtRef.current = 0;
        lastFeedPointerDownAtRef.current = 0;
        return;
      }
      if (event.target?.closest?.('[data-chat-message-feed="true"]')) {
        // A chat-feed drag may make WKWebView drop DOM focus without closing
        // the keyboard. Treat it as composer interaction, not an intentional
        // request to dismiss the keyboard.
        lastFeedPointerDownAtRef.current = Date.now();
        feedGestureActiveRef.current = true;
        if (event.type === "touchstart") feedTouchGestureActiveRef.current = true;
        lastExternalPointerDownAtRef.current = 0;
        return;
      }
      feedGestureActiveRef.current = false;
      feedTouchGestureActiveRef.current = false;
      pendingFeedFocusRecoveryRef.current = null;
      lastFeedPointerDownAtRef.current = 0;
      lastExternalPointerDownAtRef.current = Date.now();
      if (focusRecoveryTimerRef.current != null) {
        window.clearTimeout(focusRecoveryTimerRef.current);
        focusRecoveryTimerRef.current = null;
      }
    };

    const completeFeedGesture = () => {
      if (feedScrollSettleTimerRef.current != null) {
        window.clearTimeout(feedScrollSettleTimerRef.current);
        feedScrollSettleTimerRef.current = null;
      }
      if (!feedGestureActiveRef.current) return;
      feedGestureActiveRef.current = false;
      feedTouchGestureActiveRef.current = false;

      const input = pendingFeedFocusRecoveryRef.current;
      pendingFeedFocusRecoveryRef.current = null;
      if (!input) return;

      scheduleComposerFocusRecovery(input, {
        allowFeedElement: true,
        allowWebKeyboard: !isNativeApp(),
        immediate: true,
      });
    };

    const scheduleCancelledTouchRecovery = () => {
      if (feedScrollSettleTimerRef.current != null) {
        window.clearTimeout(feedScrollSettleTimerRef.current);
      }
      feedScrollSettleTimerRef.current = window.setTimeout(
        completeFeedGesture,
        CANCELLED_FEED_TOUCH_SETTLE_MS
      );
    };

    const finishFeedGesture = (event) => {
      if (!feedGestureActiveRef.current) return;
      // The browser cancels pointer events when a touch starts scrolling.
      // Some WebViews cancel the touch sequence too; wait for feed scrolling
      // to settle before restoring focus in that case.
      if (feedTouchGestureActiveRef.current) {
        if (event.type === "touchcancel") {
          scheduleCancelledTouchRecovery();
          return;
        }
        if (event.type !== "touchend" || event.touches?.length) return;
      }
      completeFeedGesture();
    };

    const handleFeedScrollDuringCancelledTouch = (event) => {
      if (
        feedGestureActiveRef.current &&
        feedScrollSettleTimerRef.current != null &&
        event.target?.closest?.('[data-chat-message-feed="true"]')
      ) {
        scheduleCancelledTouchRecovery();
      }
    };

    document.addEventListener("pointerdown", rememberExternalPointerDown, true);
    document.addEventListener("touchstart", rememberExternalPointerDown, true);
    document.addEventListener("pointerup", finishFeedGesture, true);
    document.addEventListener("pointercancel", finishFeedGesture, true);
    document.addEventListener("touchend", finishFeedGesture, true);
    document.addEventListener("touchcancel", finishFeedGesture, true);
    document.addEventListener("scroll", handleFeedScrollDuringCancelledTouch, true);
    return () => {
      document.removeEventListener("pointerdown", rememberExternalPointerDown, true);
      document.removeEventListener("touchstart", rememberExternalPointerDown, true);
      document.removeEventListener("pointerup", finishFeedGesture, true);
      document.removeEventListener("pointercancel", finishFeedGesture, true);
      document.removeEventListener("touchend", finishFeedGesture, true);
      document.removeEventListener("touchcancel", finishFeedGesture, true);
      document.removeEventListener("scroll", handleFeedScrollDuringCancelledTouch, true);
      feedGestureActiveRef.current = false;
      feedTouchGestureActiveRef.current = false;
      pendingFeedFocusRecoveryRef.current = null;
      if (feedScrollSettleTimerRef.current != null) {
        window.clearTimeout(feedScrollSettleTimerRef.current);
        feedScrollSettleTimerRef.current = null;
      }
      if (focusRecoveryTimerRef.current != null) {
        window.clearTimeout(focusRecoveryTimerRef.current);
      }
    };
  }, [inputRef, scheduleComposerFocusRecovery]);

  const openWebKitFilePicker = () => {
    const input = localFileInputRef.current;
    if (!input?.isConnected) return;
    // Clearing before every picker launch also lets iOS/WKWebView emit change
    // when the same image is selected twice.
    input.value = "";
    input.click();
  };

  const openNativePhotoSource = async (source) => {
    setPhotoSourceMenuOpen(false);
    try {
      const handledNatively = await onOpenNativeImagePicker?.(source);
      if (!handledNatively) openWebKitFilePicker();
    } catch (error) {
      console.error("Could not open native chat photo picker", error);
      try {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message: "Could not open the photo picker. Please try again." },
          })
        );
      } catch {
        // ignore global error delivery failures
      }
    }
  };

  const openPhotoPicker = () => {
    if (!canUseAttachments) return;
    setAttachmentMenuOpen(false);
    setStickerPanelOpen(false);

    if (usesNativePhotoSourceMenu) {
      setPhotoSourceMenuOpen(true);
      return;
    }

    openWebKitFilePicker();
  };

  const openPollSheet = () => {
    if (!canUseSecondaryAttachments) return;
    setAttachmentMenuOpen(false);
    onOpenPoll?.();
  };

  const openLocationSheet = () => {
    if (!canUseSecondaryAttachments) return;
    setAttachmentMenuOpen(false);
    onOpenLocation?.();
  };

  const wrapperClassName = embedded
    ? "relative z-50 w-full px-0 pb-0 [-webkit-text-size-adjust:100%] [text-size-adjust:100%]"
    : "absolute left-0 right-0 bottom-[var(--bottom-nav-height)] px-3 pb-3 z-50 [-webkit-text-size-adjust:100%] [text-size-adjust:100%]";
  const hasSecondaryCta = secondaryCtaLabel && typeof onSecondaryCtaClick === "function";

  return (
    <div
      ref={wrapRef}
      className={wrapperClassName}
    >
      {showTypingIndicator && typingCount > 0 && (
        <div className="mb-2 px-1">
          <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-[#002A5D]/95 px-3 py-1.5 shadow-[0_2px_8px_rgba(0,0,0,0.2)]">
            <div className="flex -space-x-2 shrink-0">
              {visibleTypingUsers.map((u, idx) => {
                const src = buildTypingAvatar(u);
                const fallback = String(u?.code || u?.username || "?").slice(0, 1).toUpperCase();
                return (
                  <div
                    key={`${u?.id ?? idx}`}
                    className="w-6 h-6 rounded-full border border-white/80 bg-[#174C8D] overflow-hidden flex items-center justify-center"
                  >
                    {src ? (
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-[10px] font-bold">{fallback}</span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-end gap-1 shrink-0" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <span
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-white/95"
                  style={{
                    animation: "vg-typing-dot 1.1s ease-in-out infinite",
                    animationDelay: `${i * 0.18}s`,
                  }}
                />
              ))}
            </div>
            <span className="text-white text-[12px] font-semibold truncate">{typingLabel}</span>
          </div>
        </div>
      )}
      {!canSendMessages && sendDisabledMessage && hideComposerWhenDisabled ? (
        <div className="px-1">
          <div
            role="status"
            className="flex min-h-[48px] items-center justify-center rounded-2xl bg-[#002A5D]/90 px-4 py-2 text-center text-[12px] font-medium leading-[1.35] text-white shadow-[0_2px_8px_rgba(0,0,0,0.18)]"
          >
            {sendDisabledMessage}
          </div>
        </div>
      ) : null}
      {supportIdentityChoice && canSendMessages && !editingId ? (
        <div className="mb-2 flex justify-center px-4">
          <div
            className="inline-flex rounded-full border border-white/25 bg-[#15254A] p-1 text-xs font-semibold text-white shadow-[0_4px_14px_rgba(0,0,0,0.2)]"
            role="group"
            aria-label={t("chats.support.sender_mode", { defaultValue: "Send as" })}
          >
            <button
              type="button"
              onClick={() => onSupportIdentityChange?.(false)}
              aria-pressed={!sendWithPersonalPhoto}
              disabled={sending}
              className={`rounded-full px-3 py-2 disabled:opacity-60 ${!sendWithPersonalPhoto ? "bg-[#3E6FFB]" : "text-white/75"}`}
            >
              {t("chats.support.sender_support", { defaultValue: "Support" })}
            </button>
            <button
              type="button"
              onClick={() => onSupportIdentityChange?.(true)}
              aria-pressed={sendWithPersonalPhoto}
              disabled={sending}
              className={`rounded-full px-3 py-2 disabled:opacity-60 ${sendWithPersonalPhoto ? "bg-[#3E6FFB]" : "text-white/75"}`}
            >
              {t("chats.support.sender_my_photo", { defaultValue: "My photo" })}
            </button>
          </div>
        </div>
      ) : null}
      {primaryCtaLabel && typeof onPrimaryCtaClick === "function" ? (
        <div className={`mb-[10px] flex justify-center gap-2 px-5 lg:flex-wrap lg:px-1 ${hasSecondaryCta ? "flex-col lg:flex-row" : ""}`}>
          <button
            type="button"
            onClick={onPrimaryCtaClick}
            className={`vg-attendance-cta-shimmer flex min-h-[37px] items-center justify-center rounded-[40px] py-2 font-semibold leading-4 text-black shadow-[0_8px_18px_rgba(16,185,129,0.34)] transition-transform active:scale-[0.99] ${hasSecondaryCta ? "min-h-[44px] w-full px-5 text-[15px] lg:min-h-[37px] lg:min-w-[217px] lg:w-auto lg:px-6" : "min-w-[217px] max-w-full px-6 text-[15px]"}`}
          >
            {primaryCtaLabel}
          </button>
          {hasSecondaryCta ? (
            <button type="button" onClick={onSecondaryCtaClick} className="flex min-h-[44px] w-full items-center justify-center rounded-[40px] border border-white/35 bg-white/10 px-5 py-2 text-[15px] font-semibold leading-4 text-white transition hover:bg-white/15 active:scale-[0.99] lg:min-h-[37px] lg:w-auto lg:px-5 lg:text-[14px]">
              {secondaryCtaLabel}
            </button>
          ) : null}
        </div>
      ) : null}
      {allowAttachments && attachmentMenuOpen && canUseAttachments ? (
        <div
          className="pointer-events-none absolute left-1/2 z-[60] h-[100px] w-full max-w-[343px] -translate-x-1/2"
          style={
            embedded
              ? { ...attachmentMenuStyle, maxWidth: "816px", width: embeddedComposerWidth }
              : attachmentMenuStyle
          }
        >
          <button
            type="button"
            onClick={openPollSheet}
            disabled={!canUseSecondaryAttachments}
            className="pointer-events-auto absolute bottom-[50px] left-[11px] flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white text-[#0D1428] shadow-[0_8px_18px_rgba(0,0,0,0.2)] transition active:scale-95 disabled:opacity-40"
            aria-label="Attach poll"
          >
            <img src="/assets/menu/poll_icon.svg" alt="" className="h-6 w-6 object-contain" />
          </button>
          <button
            type="button"
            onClick={openPhotoPicker}
            className="pointer-events-auto absolute bottom-0 left-[11px] flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white text-[#0D1428] shadow-[0_8px_18px_rgba(0,0,0,0.2)] transition active:scale-95"
            aria-label={t("chats.event_chat.attach_aria")}
          >
            <img
              src="/assets/menu/attach.svg"
              alt=""
              className="h-6 w-6 object-contain"
              style={{ filter: CHAT_ICON_FILTER_BLACK }}
            />
          </button>
          <button
            type="button"
            onClick={openLocationSheet}
            disabled={!canUseSecondaryAttachments}
            className="pointer-events-auto absolute bottom-0 left-[61px] flex h-[46px] w-[46px] items-center justify-center rounded-full bg-white text-[#0D1428] shadow-[0_8px_18px_rgba(0,0,0,0.2)] transition active:scale-95 disabled:opacity-40"
            aria-label="Attach location"
          >
            <img src="/assets/menu/location_icon.svg" alt="" className="h-6 w-6 object-contain" />
          </button>
        </div>
      ) : null}
      {allowAttachments && photoSourceMenuOpen && canUseAttachments ? (
        <div
          ref={photoSourcePanelRef}
          role="menu"
          aria-label={t("chats.event_chat.photo_sources.title", {
            defaultValue: "Add a photo",
          })}
          className="absolute left-1/2 z-[62] w-[min(310px,calc(100%_-_28px))] -translate-x-1/2 overflow-hidden rounded-[26px] border border-white/15 bg-[#111B33]/[0.98] px-2 py-2 text-white shadow-[0_18px_46px_rgba(0,0,0,0.42)]"
          style={{ ...dimmedComposerStyle, bottom: `${composerHeight + 14}px` }}
          data-testid="native-chat-photo-source-menu"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => openNativePhotoSource("library")}
            className="flex min-h-[54px] w-full items-center gap-3 rounded-[18px] px-4 text-left text-[16px] font-medium transition hover:bg-white/10 active:bg-white/15"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="5" width="18" height="15" rx="3" stroke="currentColor" strokeWidth="1.8" />
              <path d="m5.5 17 4.2-4.4 3 3 2.6-2.6 3.2 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="16.5" cy="9.5" r="1.3" fill="currentColor" />
            </svg>
            <span>{t("chats.event_chat.photo_sources.library", { defaultValue: "Photo Library" })}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => openNativePhotoSource("camera")}
            className="flex min-h-[54px] w-full items-center gap-3 rounded-[18px] px-4 text-left text-[16px] font-medium transition hover:bg-white/10 active:bg-white/15"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M8.2 6.5 9.6 4.7h4.8l1.4 1.8H18A3 3 0 0 1 21 9.5v7A3 3 0 0 1 18 19.5H6a3 3 0 0 1-3-3v-7a3 3 0 0 1 3-3h2.2Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <circle cx="12" cy="13" r="3.4" stroke="currentColor" strokeWidth="1.8" />
            </svg>
            <span>{t("chats.event_chat.photo_sources.camera", { defaultValue: "Take Photo" })}</span>
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setPhotoSourceMenuOpen(false);
              openWebKitFilePicker();
            }}
            className="flex min-h-[54px] w-full items-center gap-3 rounded-[18px] px-4 text-left text-[16px] font-medium transition hover:bg-white/10 active:bg-white/15"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h4l1.8 2H18a2.5 2.5 0 0 1 2.5 2.5v7A2.5 2.5 0 0 1 18 19H6a2.5 2.5 0 0 1-2.5-2.5v-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
            <span>{t("chats.event_chat.photo_sources.files", { defaultValue: "Choose Files" })}</span>
          </button>
        </div>
      ) : null}
      {stickersEnabled ? (
        <div
          ref={stickerPanelRef}
          className={`absolute left-1/2 z-[58] w-[calc(100%_-_24px)] max-w-[343px] -translate-x-1/2 overflow-hidden rounded-[28px] border border-white/15 bg-[#111B33]/[0.98] shadow-[0_18px_46px_rgba(0,0,0,0.38)] transition-[opacity,transform,visibility] duration-300 ease-out ${
            stickerPanelOpen
              ? "visible pointer-events-auto translate-y-0 opacity-100"
              : "invisible pointer-events-none translate-y-5 opacity-0"
          }`}
          style={{
            bottom: `${composerHeight + 12}px`,
            ...(embedded ? { maxWidth: "816px", width: embeddedComposerWidth } : null),
          }}
          aria-hidden={!stickerPanelOpen}
          data-testid="chat-sticker-panel"
        >
          <div
            ref={stickerScrollRef}
            onScroll={updateStickerScrollMetrics}
            className="grid max-h-[280px] grid-cols-4 gap-1 overflow-y-auto overscroll-contain py-3 pl-2 pr-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="chat-sticker-scroll-region"
          >
            {CHAT_STICKERS.map((sticker) => (
              <button
                key={sticker.id}
                type="button"
                disabled={sending || !canSendMessages}
                onPointerDown={(event) => {
                  // Do not transfer focus away from an open native keyboard.
                  event.preventDefault();
                }}
                onClick={() => {
                  setStickerPanelOpen(false);
                  onSendSticker(sticker.id);
                }}
                className="aspect-square min-w-0 rounded-[18px] p-1 transition duration-150 hover:bg-white/10 active:scale-90 active:bg-white/15 disabled:opacity-45"
                aria-label={t("chats.event_chat.stickers.send", {
                  defaultValue: "Send sticker",
                })}
              >
                <img
                  src={sticker.src}
                  alt=""
                  draggable={false}
                  className="h-full w-full select-none object-contain"
                />
              </button>
            ))}
          </div>
          <div
            className={`pointer-events-none absolute bottom-3 right-[6px] top-3 w-1 rounded-full bg-white/10 transition-opacity duration-200 ${
              stickerScrollMetrics.canScroll ? "opacity-100" : "opacity-0"
            }`}
            data-testid="chat-sticker-scrollbar"
            aria-hidden="true"
          >
            <div
              className="absolute left-0 w-1 rounded-full bg-white/55 shadow-[0_0_6px_rgba(255,255,255,0.18)]"
              style={{
                height: `${stickerScrollMetrics.thumbHeight}px`,
                transform: `translateY(${stickerScrollMetrics.thumbTop}px)`,
              }}
            />
          </div>
        </div>
      ) : null}
      {!canSendMessages && sendDisabledMessage && !hideComposerWhenDisabled ? (
        <div
          className={`mx-auto min-w-0 ${
            embedded ? "max-w-none" : "w-full max-w-[343px]"
          }`}
          style={
            embedded
              ? { ...dimmedComposerStyle, width: embeddedComposerWidth }
              : dimmedComposerStyle
          }
        >
          <div
            role="status"
            className="relative flex min-h-[58px] w-full items-center justify-center rounded-[35px] border border-white/25 bg-[#7484AA] px-6 py-3 text-center text-[15px] font-medium leading-[1.35] text-white shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
          >
            <span className="max-w-full">{sendDisabledMessage}</span>
            {renderUnreadJump()}
            {renderOverlayDismiss("rounded-[35px]")}
          </div>
        </div>
      ) : !(hideComposerWhenDisabled && !canSendMessages) ? (
        <div
          className={`mx-auto flex min-w-0 items-end gap-0 ${
            embedded ? "max-w-none" : "w-full max-w-[343px]"
          }`}
          style={
            embedded
              ? { ...dimmedComposerStyle, width: embeddedComposerWidth }
              : dimmedComposerStyle
          }
        >
        {normalizedLeftActionButtons.length > 0 && (
          <div className="flex flex-col justify-end gap-2 shrink-0">
            {normalizedLeftActionButtons.map((button, index) => {
              const key = String(button?.key || button?.id || `action-${index}`);
              const disabled = !!button?.disabled;
              const active = !!button?.active;
              const ariaLabel =
                String(button?.ariaLabel || button?.title || "").trim() || undefined;

              return (
                <div key={key} className="relative w-11 h-11 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      if (disabled) return;
                      button?.onClick?.();
                    }}
                    disabled={disabled}
                    className={`w-11 h-11 rounded-full flex items-center justify-center shadow-[0_2px_4px_rgba(0,0,0,0.12)] ${
                      active
                        ? "bg-[#002A5D] text-white"
                        : "bg-white text-[#002A5D]"
                    } disabled:opacity-40`}
                    aria-label={ariaLabel}
                    title={ariaLabel}
                  >
                    {button?.icon}
                  </button>
                  {renderOverlayDismiss("rounded-full")}
                </div>
              );
            })}
          </div>
        )}

        {/* Input pill */}
        <div className="flex-1 min-w-0">
          <div
            className="relative isolate w-full rounded-[35px] border border-white/25 bg-[#7484AA] px-3 flex flex-col min-w-0 overflow-visible shadow-[0_12px_30px_rgba(0,0,0,0.16)]"
            style={{
              minHeight: COMPOSER_MIN_HEIGHT,
              WebkitBackdropFilter: "none",
              backdropFilter: "none",
            }}
          >
            {(replyTo || editingId) && (
              <div className="relative pt-2 pb-2 pl-10 pr-10 max-h-[54px] overflow-hidden">
                <div className="absolute left-9 top-2 bottom-2 w-[3px] rounded-full bg-white/70" />
                <div className="min-w-0 flex flex-col gap-[2px] pl-2 overflow-hidden">
                  {editingId ? (
                    <>
                      <div className="text-[12px] font-medium text-white leading-[14px] truncate">
                        {t("chats.event_chat.editing")}
                      </div>
                      <div className="text-[12px] text-white/70 leading-[14px] truncate">
                        {editingMeta?.preview || ""}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-[12px] font-medium text-white leading-[14px] truncate">
                        {t("chats.event_chat.reply_to", {
                          name: replyTo?.author || "",
                        })}
                      </div>
                      <div className="text-[12px] text-white/70 leading-[14px] truncate">
                        {replyTo?.preview}
                      </div>
                    </>
                  )}
                </div>

                <button
                  type="button"
                  onClick={onCancelContext}
                  className="absolute right-0 top-3 w-5 h-5 rounded-full bg-white/15 border border-white/20 flex items-center justify-center"
                  aria-label={t("chats.event_chat.cancel_context")}
                >
                  <span className="text-white text-[18px] leading-none">×</span>
                </button>
              </div>
            )}

            {pendingImages.length > 0 && (
              <div className="px-1 pt-2 pb-1">
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  {pendingImages.map((p) => (
                    <div
                      key={p.id}
                      className="relative w-16 h-16 shrink-0 rounded-[14px] overflow-hidden bg-black/10"
                    >
                      <img src={p.url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => onRemoveImage(p.id)}
                        className="absolute right-1 top-1 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center"
                      >
                        <span className="text-white text-[14px] leading-none">×</span>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {allowAttachments && (
              <>
                <button
                  type="button"
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onTouchStart={(event) => {
                    event.stopPropagation();
                  }}
                  onClick={() => {
                    if (!canInteractWithAttachments) return;
                    setStickerPanelOpen(false);
                    setPhotoSourceMenuOpen(false);
                    if (!attachmentsUnlocked) {
                      onLockedAttachmentsClick?.();
                      return;
                    }
                    if (photoOnlyAttachments) {
                      openPhotoPicker();
                      return;
                    }
                    setAttachmentMenuOpen((prev) => !prev);
                  }}
                  disabled={!canInteractWithAttachments}
                  className="touch-manipulation absolute left-[10px] bottom-[7px] z-20 flex h-11 w-11 select-none items-center justify-center rounded-full disabled:opacity-40"
                  aria-label={t("chats.event_chat.attach_aria")}
                >
                  <img
                    src={attachmentMenuOpen ? "/assets/menu/x_icon.svg" : "/assets/menu/attachment.svg"}
                    alt=""
                    className={attachmentMenuOpen ? "h-4 w-4 object-contain" : "h-[21px] w-5 object-contain"}
                    style={attachmentMenuOpen ? { opacity: 0.78 } : undefined}
                  />
                </button>
                <input
                  ref={(node) => {
                    localFileInputRef.current = node;
                    if (typeof fileInputRef === "function") {
                      fileInputRef(node);
                    } else if (fileInputRef && "current" in fileInputRef) {
                      fileInputRef.current = node;
                    }
                  }}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickImages}
                  className="pointer-events-none absolute h-px w-px opacity-0"
                  tabIndex={-1}
                />
              </>
            )}

            <textarea
              ref={(node) => {
                if (typeof inputRef === "function") {
                  inputRef(node);
                } else if (inputRef && "current" in inputRef) {
                  inputRef.current = node;
                }
                localInputRef.current = node;
              }}
              rows={1}
              defaultValue={inputValue}
              onInput={handlePatchedChange}
              onKeyDown={handleKeyDown}
              onFocus={handleComposerFocus}
              onBlur={handleComposerBlur}
              onCompositionStart={() => {
                composingRef.current = true;
              }}
              onCompositionEnd={(event) => {
                composingRef.current = false;
                if (isNativeAndroid()) syncComposerDomValue(event.currentTarget);
              }}
              disabled={!canSendMessages}
              autoCapitalize="sentences"
              autoCorrect="on"
              autoComplete="off"
              inputMode="text"
              spellCheck={false}
              enterKeyHint="enter"
              placeholder={
                !canSendMessages && sendDisabledMessage
                  ? sendDisabledMessage
                  : t("chats.event_chat.input_placeholder")
              }
              wrap="soft"
              className={visibleComposerTextareaClass}
              style={{
                caretColor: "#ffffff",
                height: `${composerHeight}px`,
                maxHeight: `${COMPOSER_MAX_HEIGHT}px`,
                paddingTop: `${
                  composerCollapsed
                    ? COMPOSER_COLLAPSED_VERTICAL_PADDING
                    : COMPOSER_VERTICAL_PADDING
                }px`,
                paddingBottom: `${
                  composerCollapsed
                    ? COMPOSER_COLLAPSED_VERTICAL_PADDING
                    : COMPOSER_VERTICAL_PADDING
                }px`,
              }}
            />
            <textarea
              ref={measureInputRef}
              aria-hidden="true"
              tabIndex={-1}
              readOnly
              rows={1}
              defaultValue={inputValue}
              wrap="soft"
              className={measureComposerTextareaClass}
              style={{
                height: "auto",
                minHeight: 0,
                maxHeight: "none",
                paddingTop: `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`,
                paddingBottom: `${COMPOSER_COLLAPSED_VERTICAL_PADDING}px`,
              }}
            />

            {renderUnreadJump()}

            {stickersEnabled ? (
              <button
                ref={stickerToggleRef}
                type="button"
                onPointerDown={(event) => {
                  // Opening the tray should not begin a keyboard resize cycle.
                  event.preventDefault();
                }}
                onClick={toggleStickerPanel}
                disabled={!canSendMessages || !!editingId || sending}
                className={`absolute right-[52px] bottom-[7px] z-20 flex h-11 w-10 items-center justify-center rounded-full text-white transition duration-200 active:scale-90 disabled:opacity-35 ${
                  stickerPanelOpen ? "bg-white/14" : "bg-transparent"
                }`}
                aria-label={t(
                  stickerPanelOpen
                    ? "chats.event_chat.stickers.keyboard"
                    : "chats.event_chat.stickers.toggle",
                  { defaultValue: stickerPanelOpen ? "Show keyboard" : "Open stickers" }
                )}
                aria-expanded={stickerPanelOpen}
              >
                <img
                  src="/assets/menu/face-smile.svg"
                  alt=""
                  className="h-6 w-6 object-contain opacity-50"
                  style={{ filter: CHAT_ICON_FILTER_WHITE }}
                  data-testid="chat-sticker-toggle-icon"
                />
              </button>
            ) : null}

            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className="absolute right-[12px] bottom-[9px] h-[40px] w-[40px] rounded-full bg-[#3E6FFB] flex items-center justify-center shadow-[0_8px_18px_rgba(62,111,251,0.36)] disabled:opacity-40"
              aria-label={t("chats.event_chat.send_aria")}
            >
              {editingId ? (
                <img
                  src="/assets/menu/check.svg"
                  alt=""
                  className="h-6 w-6"
                  style={{ filter: CHAT_ICON_FILTER_WHITE }}
                />
              ) : (
                <svg width="25" height="25" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 5.25C12.42 5.25 12.81 5.47 13.03 5.83L18.76 15.15C19.26 15.97 18.67 17.02 17.71 17.02H6.29C5.33 17.02 4.74 15.97 5.24 15.15L10.97 5.83C11.19 5.47 11.58 5.25 12 5.25Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="1.2"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {renderOverlayDismiss("rounded-[35px]")}
          </div>
        </div>
        </div>
      ) : null}
    </div>
  );
}
